import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Target, CheckCircle2, Loader2, Ruler } from 'lucide-react';

import Button from '../../../components/ui/Button';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const Card = styled.div`
  background: #fff; border: 1px solid #E8ECF0; border-radius: 12px; padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); display: flex; flex-direction: column; gap: 10px;
`;
const Head = styled.div`display: flex; align-items: flex-start; gap: 12px;`;
const IconWrap = styled.div`
  width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(2, 211, 191, 0.12); color: #01A899;
`;
const Title = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const Meta = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 999px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
`;
const Desc = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little}; white-space: pre-wrap; line-height: 1.5;
`;
const Criteria = styled.div`
  display: flex; align-items: flex-start; gap: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor}; border-radius: 8px; padding: 8px 10px;
`;
const Actions = styled.div`display: flex; gap: 8px; margin-top: 4px;`;
const Empty = styled.div`
  text-align: center; padding: 48px 16px; color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family}; font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

export default function GoalsTab({ goals, onAgree }) {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState(null);

  const runAgree = async (id) => { setBusyId(id); await onAgree(id); setBusyId(null); };

  const list = Array.isArray(goals) ? goals : [];

  return (
    <Wrap>
      {list.length === 0 && <Empty>{t('myOnboarding.goals.empty')}</Empty>}

      {list.map((goal) => {
        const agreed = !!goal.agreedByEmployeeAt;
        const isBusy = busyId === goal.id;
        return (
          <Card key={goal.id}>
            <Head>
              <IconWrap><Target size={18} /></IconWrap>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Title>{goal.title}</Title>
                <Meta>
                  {goal.period && (
                    <Badge $bg="rgba(124,58,237,0.10)" $color="#7C3AED">
                      {t(`onboardingOneOnOne.period.${goal.period}`, { defaultValue: goal.period })}
                    </Badge>
                  )}
                  <Badge $bg="rgba(2,145,239,0.10)" $color="#0291EF">
                    {t(`myOnboarding.goals.status.${goal.status}`, { defaultValue: goal.status })}
                  </Badge>
                  {goal.managerScore != null && (
                    <span>{t('myOnboarding.goals.managerScore')}: <strong>{goal.managerScore}</strong></span>
                  )}
                </Meta>
              </div>
            </Head>

            {goal.description && <Desc>{goal.description}</Desc>}
            {goal.measurementCriteria && (
              <Criteria>
                <Ruler size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{goal.measurementCriteria}</span>
              </Criteria>
            )}

            {agreed ? (
              <Badge $bg="rgba(2,211,191,0.12)" $color="#01A899" style={{ alignSelf: 'flex-start' }}>
                <CheckCircle2 size={14} />{t('myOnboarding.goals.agreed')}
              </Badge>
            ) : (
              <Actions>
                <Button variant="primary" disabled={isBusy} onClick={() => runAgree(goal.id)}>
                  {isBusy ? <Loader2 size={16} /> : t('myOnboarding.goals.action.agree')}
                </Button>
              </Actions>
            )}
          </Card>
        );
      })}
    </Wrap>
  );
}
